import styled from '@emotion/styled'

import type { FC, PropsWithChildren } from 'react'
import React, { useMemo } from 'react'
import Link from 'next/link'

import type { Options as RichTextRenderOptions } from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'

import Image from 'next/image'

import theme from '@segment/matcha/foundations/Theme'

import Badge from '@segment/matcha/components/Badge'
import Button from '@segment/matcha/components/Button'
import ButtonText from '@segment/matcha/components/ButtonText'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import IconWrapper from '@segment/matcha/components/IconWrapper'
import { ChevronLeftIcon } from '@segment/matcha/components/Icons/Common'
import { CheckIcon } from '@segment/matcha/components/Icons/Brandbook'
import LogoTile from '@segment/matcha/components/LogoTile'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import HeroBase, * as Hero from '@segment/matcha/recipes/Hero'
import RichText from '@segment/matcha/recipes/RichText'

import type { Integration } from '@segment/api/staticSiteBackend/integration'

import contentfulImageLoader from '@segment/utils/contentfulImageLoader'
import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import type { PageConfig } from '@segment/contexts/page'
import { usePageConfig } from '@segment/contexts/page'

export interface IntegrationHeroProps {
  integration: Integration
  /**
   * @ignore
   **/
  className?: string
}

const options: RichTextRenderOptions = {
  renderNode: {
    [BLOCKS.UL_LIST]: (_, children) => (
      <ColumnTrackWrapper>
        <ColumnTrack gap="small" tag="ul">
          {children}
        </ColumnTrack>
      </ColumnTrackWrapper>
    ),
    [BLOCKS.LIST_ITEM]: (_, children) => (
      <StyledFeatureBox tag="li" alignment="left" headerPosition="left">
        <FeatureBox.Header>
          <IconWrapper background="white" elevation={1} variant="circle">
            <CheckIcon color={theme.palette.twilio.gray[100]} />
          </IconWrapper>
        </FeatureBox.Header>
        <FeatureBox.Body>{children}</FeatureBox.Body>
      </StyledFeatureBox>
    ),
  },
}

const createBadge = (category: Integration[`category`][number], pageConfig: PageConfig) => {
  if (!category.fields.name || !category.fields.url) {
    return
  }

  return (
    <Link key={category.fields.name} href={getAbsoluteUrl(`/catalog/${category.fields.url}#grid`, pageConfig)}>
      <StyledBadge tag="span" variant="outlined">
        {category.fields.name}
      </StyledBadge>
    </Link>
  )
}

const IntegrationHero: FC<PropsWithChildren<IntegrationHeroProps>> = ({ integration, className }) => {
  const pageConfig = usePageConfig()
  const featuresList = useMemo(() => {
    if (!integration.features) {
      return
    }

    return <RichText data={integration.features} options={options} />
  }, [integration.features])

  return (
    <Wrapper>
      <LinkWrapper>
        <Link href={getAbsoluteUrl(`/catalog`, pageConfig)}>
          <ButtonText tag="span" size="medium" iconStart={ChevronLeftIcon}>
            All integrations
          </ButtonText>
        </Link>
      </LinkWrapper>

      <HeroWrapper variant="noCopy">
        <StyledHero className={className} variant="2Col">
          <BadgeList columns={integration.category.length}>
            {integration.category.map((category) => createBadge(category, pageConfig))}
          </BadgeList>
          <HeroTitle>
            <CopyText.Heading variant="h1" tag="h1">
              {integration.name}
            </CopyText.Heading>
          </HeroTitle>
          {integration.introduction && (
            <HeroIntro>
              <CopyText.Description variant="pExtraLarge" tag="p">
                {integration.introduction}
              </CopyText.Description>
            </HeroIntro>
          )}
          <HeroCopy>
            {featuresList}

            <ButtonList>
              {integration.website && (
                <Button
                  color="primary"
                  variant="outlined"
                  size="medium"
                  tag="a"
                  href={integration.website}
                  target="_blank"
                >
                  {integration.name} website
                </Button>
              )}

              <Link href={integration.docsUrl}>
                <Button color="primary" variant="outlined" size="medium" tag="span">
                  Documentation
                </Button>
              </Link>
            </ButtonList>
          </HeroCopy>

          <HeroImage>
            {integration.logo && (
              <StyledLogoTile size="fill" variant="solid" elevation={1}>
                <Image
                  loader={contentfulImageLoader}
                  src={integration.logo.fields.file.url}
                  alt={integration.name}
                  width={160}
                  height={160}
                  priority={true}
                />
              </StyledLogoTile>
            )}
          </HeroImage>
        </StyledHero>
      </HeroWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding-top: 70px;
  background: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const HeroWrapper = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 32px;
    padding-bottom: 32px;
  }
`

const LinkWrapper = styled.div`
  max-width: 1440px;
  padding: 0 24px;
  margin: 0 auto;

  ${theme.breakpoints.up(`sm`)} {
    padding: 0 40px;
  }

  ${theme.breakpoints.up(`lg`)} {
    padding: 0 80px;
  }
`

const StyledHero = styled(HeroBase)`
  position: relative;
  padding-bottom: 60px;

  ${Hero.Wrapper} {
    align-items: start;
    gap: 0 24px;
    grid-template-areas:
      'badges badges'
      'title image'
      'intro intro'
      'copy copy';
    grid-template-columns: auto 1fr;
    padding: 0;

    ${theme.breakpoints.up(`md`)} {
      column-gap: 40px;
      grid-template-areas:
        'badges badges'
        'title image'
        'intro image'
        'copy copy';
    }

    ${theme.breakpoints.up(`lg`)} {
      grid-template-areas:
        'badges badges'
        'title image'
        'intro image'
        'copy image';
    }

    ${theme.breakpoints.up(`xl`)} {
      gap: 0 160px;
    }
  }
`

interface BadgeListProps {
  columns: number
}
const BadgeList = styled.div<BadgeListProps>`
  grid-area: badges;
  margin-bottom: 20px;
  text-align: left;
`

const StyledBadge = styled(Badge)`
  margin: 4px;
`

const HeroTitle = styled(CopyTextBase)`
  align-self: center;
  grid-area: title;

  ${theme.breakpoints.up(`md`)} {
    margin-bottom: 8px;
  }
`

const HeroIntro = styled(CopyTextBase)`
  grid-area: intro;

  ${theme.breakpoints.down(`sm`)} {
    margin-top: 16px;
  }
`

const HeroCopy = styled(Hero.Copy)``

const ColumnTrackWrapper = styled.div`
  margin: 56px 0;

  padding: 0;
`

const StyledFeatureBox = styled(FeatureBoxBase)`
  p {
    margin: 0;
    align-self: center;
  }
`

const ButtonList = styled.div`
  display: grid;
  gap: 24px;
  margin-top: 56px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    grid-template-columns: repeat(2, max-content);
  }
`

const HeroImage = styled(Hero.Contents)`
  display: none;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    display: grid;
    justify-self: flex-end;
    max-width: 100%;
    width: 160px;
    grid-area: image;
  }

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    width: 240px;
  }
`

const StyledLogoTile = styled(LogoTile)`
  padding: 16%;
`

export default IntegrationHero
